import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { ExportModalPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { TrackingProperties, track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateUIToBackend, i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import ReportsForm from 'modules/employees/lib/forms/Reports'
import { useModule as useTimeoffModule } from 'modules/timeoff/hooks/useModule'

import DateRange, { DATERANGES } from './ExportFields/DateRange'
import TimeoffTypeSelect, { TimeoffExportType } from './TimeoffTypeSelect'

enum ExportType {
  Employees = 'employees',
  Timeoff = 'timeoff',
  WorkedDays = 'worked_days',
}

interface Props {
  onClose: () => void
  loading: boolean
}

interface ExportOption {
  label: string
  value: string
}

interface ScheduleTimeoffForm {
  from: string
  to: string
  leave_type_ids: number[]
  employee_selection: EmployeeSelectorV2MultiSelectState
}

interface ActivityTimeoffForm {
  from: string
  to: string
  leave_type_ids: number[]
  employee_selection: EmployeeSelectorV2MultiSelectState
  trim_leave_duration: boolean
}

interface UsedTimeoffForm {
  from: string
  to: string
  employee_selection: EmployeeSelectorV2MultiSelectState
}

interface CompanyTimeoffForm {
  employee_selection: EmployeeSelectorV2MultiSelectState
  reference_date: string
}

interface GenericExportForm {
  reference_date: string
}

const ExportEmployeesModalPage = ({ onClose, loading }: Props) => {
  const { loading: loadingTimeoff, enabled: timeoffEnabled } = useTimeoffModule()
  const { loading: loadingExportData, data } = useQuery(ExportModalPageDocument)
  const canExportEmployees = data?.employees._policies.exportEmployees.can ?? false
  const canDownloadLeaves = data?.apiCore._policies.accessDownloadLeaves.can ?? false
  const loadingData = loadingTimeoff || loadingExportData

  const exportOptions = React.useMemo(() => {
    const options: ExportOption[] = []
    if (!loadingData) {
      if (canExportEmployees) {
        options.push({
          label: i18n.t('employees.reports.employees'),
          value: ExportType.Employees,
        })
        options.push({
          label: i18n.t('employees.reports.worked_days'),
          value: ExportType.WorkedDays,
        })
      }

      if (timeoffEnabled && canDownloadLeaves) {
        options.push({
          label: i18n.t('employees.reports.timeoff'),
          value: ExportType.Timeoff,
        })
      }
    }

    return options
  }, [timeoffEnabled, loadingData])

  useUnsafeEffect(() => {
    const [firstOption] = exportOptions
    if (firstOption) {
      reportForm.get('type').set(firstOption.value)
    }
  }, [exportOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  const reportForm = React.useRef(
    new ReportsForm({
      type: exportOptions[0]?.value,
      reference_date: i18nDateToString(moment(), 'backend'),
      daterange: DATERANGES.current_month,
      from: i18nDateToString(moment().startOf('month'), 'backend'),
      to: i18nDateToString(moment().add(1, 'M').startOf('month'), 'backend'),
      employee_ids: [],
      leave_type_ids: [],
      trim_leave_duration: false,
      kind: TimeoffExportType.companyTimeoff,
    })
  ).current

  const employeeSelectionRef = React.useRef<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  const handleEmployeeSelectionChange = React.useCallback(
    (selector: EmployeeSelectorV2MultiSelectState) => {
      employeeSelectionRef.current = selector
    },
    []
  )

  const trackOnSubmit = (
    exportType: ExportType,
    timeoffType?: TimeoffExportType,
    employeeIds?: number[],
    leaveTypeIds?: number[],
    trimLeaveDuration?: boolean
  ) => {
    const properties: TrackingProperties = {
      export_type: exportType,
    }

    if (timeoffType) {
      properties.export_type_timeoff = timeoffType

      const addEmployeesProperty = [
        TimeoffExportType.activityTimeoff,
        TimeoffExportType.companyTimeoff,
        TimeoffExportType.scheduleTimeoff,
      ].includes(timeoffType)

      const addAbsenceTypesProperty = [
        TimeoffExportType.activityTimeoff,
        TimeoffExportType.scheduleTimeoff,
      ].includes(timeoffType)

      if (addEmployeesProperty) {
        properties.number_of_employees = employeeIds?.length
      }
      if (addAbsenceTypesProperty) {
        properties.number_of_absence_types = leaveTypeIds?.length
      }
      if (timeoffType === TimeoffExportType.activityTimeoff) {
        properties.trimmed_absence = !!trimLeaveDuration
      }
    }

    track(trackingEvents.timeoff.exportEmployeeListSubmit, properties)
  }

  const buildScheduleTimeoffForm = () => {
    const body: ScheduleTimeoffForm = {
      from: dateUIToBackend(reportForm.get('from').value),
      to: dateUIToBackend(reportForm.get('to').value),
      leave_type_ids: reportForm.get('leave_type_ids').value,
      employee_selection: employeeSelectionRef.current,
    }
    return body
  }

  const buildActivityTimeoffForm = () => {
    const body: ActivityTimeoffForm = {
      from: dateUIToBackend(reportForm.get('from').value),
      to: dateUIToBackend(reportForm.get('to').value),
      leave_type_ids: reportForm.get('leave_type_ids').value,
      employee_selection: employeeSelectionRef.current,
      trim_leave_duration: reportForm.get('trim_leave_duration').value,
    }
    return body
  }

  const buildCompanyTimeoffForm = () => {
    const body: CompanyTimeoffForm = {
      employee_selection: employeeSelectionRef.current,
      reference_date: dateUIToBackend(reportForm.get('reference_date').value),
    }
    return body
  }

  const buildUsedTimeoffForm = () => {
    const body: UsedTimeoffForm = {
      from: dateUIToBackend(reportForm.get('from').value),
      to: dateUIToBackend(reportForm.get('to').value),
      employee_selection: employeeSelectionRef.current,
    }
    return body
  }

  const validateEmployeeSelection = () => {
    const selection = employeeSelectionRef.current

    if (selection.type === 'all') {
      return true
    }

    // If type is 'manual' and no ids, show error
    if (selection.type === 'manual') {
      if (!selection.ids || selection.ids.length === 0) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('employees.reports.error.employee_ids_empty'),
        })
        return false
      }
      return true
    }

    app.setFlash({
      type: 'wrong',
      message: i18n.t('employees.reports.error.employee_ids_empty'),
    })
    return false
  }

  const validateCompanyTimeoff = () => {
    return validateEmployeeSelection()
  }

  const validateUsedTimeoff = () => {
    return validateEmployeeSelection()
  }

  const validateScheduleTimeoff = () => {
    if (!validateEmployeeSelection()) {
      return false
    }
    if (reportForm.get('leave_type_ids').value.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.reports.error.leave_types_empty'),
      })
      return false
    }
    return true
  }

  const validateActivityTimeoff = () => {
    if (!validateEmployeeSelection()) {
      return false
    }
    if (reportForm.get('leave_type_ids').value.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.reports.error.leave_types_empty'),
      })
      return false
    }
    return true
  }

  const { running, performAction: onSubmit } = useAsyncAction(
    async () => {
      const type = reportForm.get('type').value
      const api = ApiClient()

      switch (type) {
        case ExportType.Employees:
          trackOnSubmit(type)

          const { result: isDownloadable } = await api.get('/export/employees/download_available')
          if (isDownloadable) {
            window.location.href = buildUrl('/export/employees')
          } else {
            app.setFlash({
              type: 'correct',
              message: i18n.t('employees.reports.email_will_send'),
            })
          }
          break

        case ExportType.Timeoff: {
          const kind = reportForm.get('kind').value

          let body:
            | CompanyTimeoffForm
            | ScheduleTimeoffForm
            | ActivityTimeoffForm
            | UsedTimeoffForm
            | GenericExportForm

          switch (kind) {
            case TimeoffExportType.companyTimeoff:
              if (!validateCompanyTimeoff()) return false
              body = buildCompanyTimeoffForm()
              break
            case TimeoffExportType.usedTimeoff:
              if (!validateUsedTimeoff()) return false
              body = buildUsedTimeoffForm()
              break
            case TimeoffExportType.scheduleTimeoff:
              if (!validateScheduleTimeoff()) return false
              body = buildScheduleTimeoffForm()
              break
            case TimeoffExportType.activityTimeoff:
              if (!validateActivityTimeoff()) return false
              body = buildActivityTimeoffForm()
              break
            default:
              body = { reference_date: dateUIToBackend(reportForm.get('reference_date').value) }
              break
          }

          const { result } = await api.post(`/timeoff/exports/${kind}/request_download`, body)

          if (result) {
            trackOnSubmit(
              type,
              kind,
              reportForm.get('employee_ids').value,
              reportForm.get('leave_type_ids').value,
              reportForm.get('trim_leave_duration').value
            )

            app.setFlash({
              type: 'correct',
              message: i18n.t('employees.reports.email_will_send'),
            })
          }

          break
        }

        case ExportType.WorkedDays: {
          let from, to

          if (reportForm.get('daterange').value === DATERANGES.current_month) {
            from = i18nDateToString(moment().startOf('month'), 'backend')
            to = i18nDateToString(moment(), 'backend')
          } else if (reportForm.get('daterange').value === DATERANGES.current_year) {
            from = i18nDateToString(moment().startOf('year'), 'backend')
            to = i18nDateToString(moment(), 'backend')
          } else {
            from = dateUIToBackend(reportForm.get('from').value)
            to = dateUIToBackend(reportForm.get('to').value)
          }

          const { result } = await api.get(`/export/worked_days?from=${from}&to=${to}`)

          if (result) {
            trackOnSubmit(type)

            window.location.href = buildUrl(`/export/worked_days/download?from=${from}&to=${to}`)
          } else {
            trackOnSubmit(type)

            app.setFlash({
              type: 'correct',
              message: i18n.t('employees.reports.email_will_send'),
            })
          }

          break
        }
      }
    },
    {
      successFlash: false,
      errorFlash: true,
    }
  )

  useEffect(() => {
    track(trackingEvents.timeoff.exportEmployeeListStart)
  }, [])

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('employees.reports.title')}
      description={i18n.t('employees.reports.description_generic')}
      primaryAction={{
        label: i18n.t('employees.reports.download'),
        onClick: onSubmit,
        disabled: running || loadingData,
        loading: running,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
      loading={loadingData || loading}
    >
      <Box gap="s16">
        {!loadingData && !loading ? (
          <>
            <FormRow>
              <Select
                label={i18n.t('employees.reports.type')}
                field={reportForm.get('type')}
                options={exportOptions}
              />
            </FormRow>
            {reportForm.get('type').value === ExportType.Timeoff && (
              <TimeoffTypeSelect
                form={reportForm}
                onEmployeeSelectionChange={handleEmployeeSelectionChange}
              />
            )}
            {reportForm.get('type').value === ExportType.WorkedDays && (
              <DateRange form={reportForm} />
            )}
          </>
        ) : (
          <Loading />
        )}
      </Box>
    </F0Dialog>
  )
}

export default observer(ExportEmployeesModalPage)
